import React from "react";
import { Link } from "react-router-dom";
import {
  getRoomOccupancyStatus,
  formatOccupancyStatusDe,
  isActiveRoom,
} from "../utils/unitOccupancyStatus";
import { getUnitAddressSubtitle, getUnitHeadingLabel } from "../utils/unitDisplayId";
import {
  getRoomDetailPath,
  openRoomLabel,
} from "../pages/admin/rooms/roomConstants";

/**
 * The status a RETIRED room shows, in place of an occupancy label.
 *
 * SENTINEL, NOT A KEY. `formatOccupancyStatusDe` returns an unrecognised key
 * verbatim (`m[key] || key`), so the display string IS the value and reads
 * capitalised wherever it is formatted. `AdminUnitDetailPage` uses the same
 * word in its own `presentedRoomStatus`, which is why the map, the room table
 * and the room-status overview can never disagree about it.
 */
const INACTIVE_ROOM_STATUS = "Inaktiv";

/**
 * What this room's Status line says — activity first, occupancy second.
 *
 * `isActiveRoom` is the only activity truth. NOT `room.status`: the wire sends
 * `status = getattr(r, "status", None) or "Frei"`, so a retired room's `status`
 * reads "Frei" and would present stood-down capacity as available.
 *
 * A retired room keeps its tenancies and its history untouched; this decides
 * one label. An inactive room that is still let therefore reads "Inaktiv"
 * rather than "Belegt" — presentation precedence, not a data change.
 */
function presentedRoomStatus(room, tenancies) {
  if (!isActiveRoom(room)) return INACTIVE_ROOM_STATUS;
  return tenancies != null ? getRoomOccupancyStatus(room, tenancies) : null;
}

/**
 * THE TONE VOCABULARY IS NOT NEW. Every class below is already in use for the
 * same four states elsewhere in the admin surface: the emerald/amber/rose
 * light steps and `border-*-200` come from `pages/admin/unit/Badge.js` (the
 * badge the room table and the room-status overview render for these very
 * rooms), the `dark:` halves from `STATUS_TONES` in
 * `pages/admin/rooms/CoLivingRoomCard.jsx`, which is the only existing map
 * keyed by these exact German keys AND carrying an `inaktiv` entry. Its
 * comment states the intent this reuses: a stood-down room shares the muted
 * pair because it "is not free (rose would read as 'fill this')".
 *
 * Every light palette step is paired with a `dark:` twin inside the SAME
 * returned string, which is what makes a palette colour theme-correct and
 * what the guard in RoomMap.test.jsx checks.
 */
function getRoomCardClasses(occ) {
  if (occ === INACTIVE_ROOM_STATUS) {
    // Neutral on purpose. A retired room is not free, so it must not wear the
    // free tone it wore before ROOM-ACT-2b.
    return "border-border bg-muted";
  }
  if (occ === "belegt") {
    return "border-emerald-200 bg-emerald-50 dark:border-emerald-500/20 dark:bg-emerald-500/10";
  }
  if (occ === "reserviert") {
    return "border-amber-200 bg-amber-50 dark:border-amber-500/20 dark:bg-amber-500/10";
  }
  if (occ === "frei") {
    return "border-rose-200 bg-rose-50 dark:border-rose-500/20 dark:bg-rose-500/10";
  }
  // UNKNOWN KEEPS THE FREE TONE, deliberately unchanged. Which tone an
  // unmeasured room should wear is a status decision, not a colour decision,
  // and this slice only swaps colour values.
  return "border-rose-200 bg-rose-50 dark:border-rose-500/20 dark:bg-rose-500/10";
}

/**
 * The dot takes the status word's own colour via `currentColor`, rather than
 * repeating it. That is why this no longer carries its own colour table: the
 * dot and the word it sits beside are now the same colour BY CONSTRUCTION and
 * cannot drift apart, and the migration adds no fifth, sixth and seventh
 * colour value for the dots. The decision per input is unchanged.
 */
function getStatusDotClass(occ) {
  return `bg-current ${getStatusValueClass(occ)}`;
}

/**
 * THE LIGHT HALF IS THE -800 STEP, NOT -700, and that is a measurement rather
 * than a preference. On the tinted card the -700 steps measured 5.21:1
 * (emerald) and 4.84:1 (amber) against WCAG AA's 4.5:1 — passing, with almost
 * no margin on amber. `pages/admin/unit/Badge.js` already renders these same
 * four room states on this same page and already uses the -800 step for its
 * light halves, so the more accessible class was in the vocabulary already.
 */
function getStatusValueClass(occ) {
  if (occ === INACTIVE_ROOM_STATUS) return "text-muted-foreground";
  if (occ === "belegt") return "text-emerald-800 dark:text-emerald-300";
  if (occ === "reserviert") return "text-amber-800 dark:text-amber-300";
  return "text-rose-800 dark:text-rose-300";
}

function getStatusLabelFromOcc(occ) {
  if (occ == null) return "—";
  return formatOccupancyStatusDe(occ);
}

function RoomMap({
  unit,
  rooms: allRooms = [],
  tenancies = null,
  isCollapsed = false,
  onToggleHeader = null,
  headerTrailingAction = null,
}) {
  const unitRooms = allRooms.filter((room) => (room.unitId || room.unit_id) === (unit.unitId || unit.id));

  // TWO POPULATIONS, DELIBERATELY DIFFERENT.
  //
  // The cards below render `unitRooms` — the whole inventory, retired rooms
  // included, because an admin has to be able to see what was stood down.
  //
  // The three counters below describe the ACTIVE RENTABLE CAPACITY, so they
  // classify `activeUnitRooms` only. Before this slice they classified every
  // row, which meant a retired room added itself to `Frei` (it has no tenancy)
  // or to `Belegt` (a historic row that still does) and inflated capacity that
  // does not exist. `isActiveRoom` is the same activity truth the room table
  // and the room-status overview use.
  //
  // Derived from `unitRooms`, not from `allRooms`, so the unit match stays
  // byte-for-byte the one this component has always applied.
  const activeUnitRooms = unitRooms.filter(isActiveRoom);
  // Counted as the remainder rather than by re-filtering, so it can never
  // disagree with the population the capacity counters left out.
  const inactiveCount = unitRooms.length - activeUnitRooms.length;

  const occupiedCount =
    tenancies == null
      ? 0
      : activeUnitRooms.filter(
          (room) => getRoomOccupancyStatus(room, tenancies) === "belegt"
        ).length;
  const reservedCount =
    tenancies == null
      ? 0
      : activeUnitRooms.filter(
          (room) => getRoomOccupancyStatus(room, tenancies) === "reserviert"
        ).length;
  const freeCount =
    tenancies == null
      ? activeUnitRooms.length
      : activeUnitRooms.filter(
          (room) => getRoomOccupancyStatus(room, tenancies) === "frei"
        ).length;

  const headerInteractive = typeof onToggleHeader === "function";
  const headerClass = `flex flex-wrap items-center gap-[10px] border-b border-border px-[14px] py-[11px] ${
    headerInteractive ? "cursor-pointer select-none hover:bg-muted" : ""
  }`;

  if (unitRooms.length === 0) {
    return (
      <div className="overflow-hidden rounded-[10px] border border-border bg-card">
        <div
          className={headerClass}
          onClick={headerInteractive ? onToggleHeader : undefined}
          onKeyDown={
            headerInteractive
              ? (e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onToggleHeader();
                  }
                }
              : undefined
          }
          role={headerInteractive ? "button" : undefined}
          tabIndex={headerInteractive ? 0 : undefined}
        >
          {headerInteractive ? (
            <span className="shrink-0 text-[10px] text-muted-foreground" aria-hidden>
              {isCollapsed ? "▼" : "▲"}
            </span>
          ) : null}
          <div className="min-w-0 flex-1">
            <p className="font-mono text-[11px] font-medium text-primary">{getUnitHeadingLabel(unit)}</p>
            <p className="mt-[2px] text-[11px] text-muted-foreground">{getUnitAddressSubtitle(unit)}</p>
          </div>
          {headerTrailingAction ? (
            <div className="ml-auto shrink-0">{headerTrailingAction}</div>
          ) : null}
        </div>
        {!isCollapsed ? (
          <p className="px-[12px] py-[12px] text-[11px] italic text-muted-foreground">Keine Rooms für diese Unit erfasst.</p>
        ) : null}
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-[10px] border border-border bg-card">
      <div
        className={headerClass}
        onClick={headerInteractive ? onToggleHeader : undefined}
        onKeyDown={
          headerInteractive
            ? (e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onToggleHeader();
                }
              }
            : undefined
        }
        role={headerInteractive ? "button" : undefined}
        tabIndex={headerInteractive ? 0 : undefined}
      >
        {headerInteractive ? (
          <span className="shrink-0 text-[10px] text-muted-foreground" aria-hidden>
            {isCollapsed ? "▼" : "▲"}
          </span>
        ) : null}
        <div className="min-w-0 flex-1">
          <p className="font-mono text-[11px] font-medium text-primary">{getUnitHeadingLabel(unit)}</p>
          <p className="mt-[2px] text-[11px] text-muted-foreground">{getUnitAddressSubtitle(unit)}</p>
        </div>
        <div className="ml-auto flex flex-wrap gap-[6px]">
          <span className="rounded-full border border-border bg-muted px-2 py-[2px] text-[9px] font-semibold text-muted-foreground">
            {unitRooms.length} Rooms
          </span>
          <span className="rounded-full border border-emerald-200 bg-emerald-100 px-2 py-[2px] text-[9px] font-semibold text-emerald-800 dark:border-emerald-500/20 dark:bg-emerald-500/15 dark:text-emerald-300">
            {occupiedCount} Belegt
          </span>
          <span className="rounded-full border border-amber-200 bg-amber-100 px-2 py-[2px] text-[9px] font-semibold text-amber-800 dark:border-amber-500/20 dark:bg-amber-500/15 dark:text-amber-300">
            {reservedCount} Reserviert
          </span>
          <span className="rounded-full border border-rose-200 bg-rose-100 px-2 py-[2px] text-[9px] font-semibold text-rose-800 dark:border-rose-500/20 dark:bg-rose-500/15 dark:text-rose-300">
            {freeCount} Frei
          </span>
          {/*
            INVENTORY INFORMATION, NOT A CAPACITY COUNT. The map keeps showing
            every room, so without this chip a unit reads "4 Rooms" above
            "1 Belegt + 0 Reserviert + 1 Frei" with nothing to account for the
            other two. Rendered only when there is a gap to explain — a
            permanent "0 Inaktiv" on every unit is noise, and 390px has four
            chips to fit already.
          */}
          {inactiveCount > 0 ? (
            <span className="rounded-full border border-border bg-muted px-2 py-[2px] text-[9px] font-semibold text-muted-foreground">
              {inactiveCount} Inaktiv
            </span>
          ) : null}
          {headerTrailingAction}
        </div>
      </div>

      {!isCollapsed ? (
      <div className="flex flex-wrap gap-[8px] px-[12px] py-[12px]">
        {unitRooms.map((room, index) => {
          const occ = presentedRoomStatus(room, tenancies);
          const roomName = room.roomName || room.name || `Zimmer ${index + 1}`;
          // D4c2: the tile becomes the way into the room. A row without
          // an id stays a plain <div> rather than linking to
          // /admin/rooms/undefined, which would look clickable and 404.
          const detailPath = getRoomDetailPath(room.roomId || room.id);
          const Tile = detailPath ? Link : "div";
          const tileProps = detailPath
            ? { to: detailPath, "aria-label": openRoomLabel(roomName) }
            : {};
          const interactive = detailPath
            ? " block transition-colors hover:border-foreground/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            : "";
          return (
            <Tile
              key={room.roomId || index}
              {...tileProps}
              className={`flex-1 min-w-[150px] rounded-[9px] border p-[12px_14px] ${getRoomCardClasses(occ)}${interactive}`}
            >
              <p className="mb-[8px] flex items-center gap-[6px] text-[12px] font-medium text-foreground">
                <span className={`h-[6px] w-[6px] shrink-0 rounded-full ${getStatusDotClass(occ)}`} />
                {roomName}
              </p>

              <div className="mb-[3px] flex items-baseline justify-between">
                <span className="text-[10px] text-muted-foreground">Status</span>
                <span className={`font-mono text-[11px] ${getStatusValueClass(occ)}`}>
                  {getStatusLabelFromOcc(occ)}
                </span>
              </div>

              {room.priceMonthly ? (
                <div className="mb-[3px] flex items-baseline justify-between">
                  <span className="text-[10px] text-muted-foreground">Miete / Mt.</span>
                  <span className="font-mono text-[11px] text-foreground">
                    CHF {Number(room.priceMonthly).toLocaleString("de-CH")}
                  </span>
                </div>
              ) : (
                <div className="mb-[3px] flex items-baseline justify-between">
                  <span className="text-[10px] text-muted-foreground">Miete / Mt.</span>
                  <span className="font-mono text-[11px] italic text-muted-foreground">—</span>
                </div>
              )}
            </Tile>
          );
        })}
      </div>
      ) : null}
    </div>
  );
}

export default RoomMap;
